import VideoDialog from '@/components/video-dialog';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { getOriginalImageUrl } from '@/util/getOriginalImageUrl';
import Image from 'next/image';
import { useMemo, useState } from 'react';
import { Carousel, CarouselContent, CarouselDots, CarouselItem } from '../ui/carousel';
import ImageCarouselPreview from './image-carousel-preview';

const ProductImageCarousel = props => {
  const { images, name, sku, seoColorLabel, videoUrl } = props;

  const sortedImages = useMemo(() => sortImages(images), [images]);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [prevState, setPrevState] = useState({
    activeIndex: 0,
    open: false,
  });
  const { isMobile } = useIsMobile();

  const generateAlt = index => {
    return [capitalizeFirstLetter(seoColorLabel), name, 'for', sku, 'image', String(index + 1)]
      .filter(Boolean)
      .join(' ');
  };

  if (sortedImages.length === 0) {
    return null;
  }

  return (
    <>
      {isMobile ? (
        <MobileCarousel
          sortedImages={sortedImages}
          generateAlt={generateAlt}
          onShowPrevDialog={index => {
            setPrevState({ activeIndex: index, open: true });
          }}
          videoUrl={videoUrl}
          onSetShowVideoDialog={setShowVideoDialog}
        />
      ) : (
        <div className='grid grid-cols-2 gap-5'>
          {sortedImages.map((x, i, arr) => {
            const alt = generateAlt(i);
            const isLastOne = i === arr.length - 1;

            return (
              <div
                key={x.uid}
                className='relative'
              >
                <Image
                  src={x.url}
                  width={1265}
                  height={1600}
                  alt={alt}
                  title={alt}
                  loader={({ src }) => src}
                  className='h-auto w-full cursor-pointer object-cover'
                  onClick={() => {
                    if (isLastOne && videoUrl) {
                      setShowVideoDialog(true);
                      return;
                    }

                    setPrevState({ activeIndex: i, open: true });
                  }}
                  unoptimized
                />

                {isLastOne && videoUrl ? (
                  <button
                    type='button'
                    className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
                    onClick={() => {
                      setShowVideoDialog(true);
                    }}
                  >
                    {videoPlayIcon}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      <ImageCarouselPreview
        activeIndex={prevState.activeIndex}
        open={prevState.open}
        setOpen={nextOpen => {
          setPrevState(x => ({ ...x, open: nextOpen }));
        }}
        imageUrls={sortedImages.map(x => getOriginalImageUrl(x.file))}
        generateAlt={generateAlt}
      />
      <VideoDialog
        open={showVideoDialog}
        url={videoUrl}
        onOpenChange={setShowVideoDialog}
      />
    </>
  );
};

const videoPlayIcon = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='144'
    height='144'
    fill='none'
    viewBox='0 0 144 144'
    className='h-auto w-[50px] lg_w-20'
  >
    <path
      fill='#fff'
      d='m67.001 47.692 25.057 20.85a4.5 4.5 0 0 1 0 6.918L67 96.31a4.5 4.5 0 0 1-7.377-3.457V51.149A4.5 4.5 0 0 1 67 47.692'
    ></path>
    <path
      stroke='#fff'
      strokeMiterlimit='10'
      strokeWidth='8'
      d='M18 72c0-29.812 24.188-54 54-54s54 24.188 54 54-24.188 54-54 54-54-24.188-54-54Z'
    ></path>
  </svg>
);

const MobileCarousel = props => {
  const { sortedImages, generateAlt, onShowPrevDialog, videoUrl, onSetShowVideoDialog } = props;
  return (
    <Carousel opts={{ loop: false }}>
      <CarouselContent className='-ml-0'>
        {sortedImages.map((x, i, arr) => {
          const alt = generateAlt(i);
          const isLastOne = i === arr.length - 1;

          return (
            <CarouselItem
              className='relative p-0'
              key={x.uid}
            >
              <Image
                src={x.url}
                width={1265}
                height={1600}
                alt={alt}
                title={alt}
                className='h-auto w-full object-cover'
                loader={({ src }) => src}
                onClick={() => {
                  if (isLastOne && videoUrl) {
                    return;
                  }

                  onShowPrevDialog(i);
                }}
                unoptimized
              />

              {isLastOne && videoUrl ? (
                <button
                  type='button'
                  className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
                  onClick={() => {
                    onSetShowVideoDialog(true);
                  }}
                >
                  {videoPlayIcon}
                </button>
              ) : null}
            </CarouselItem>
          );
        })}
      </CarouselContent>
      <CarouselDots
        className='mt-2.5 flex items-center justify-center gap-x-1'
        dotClassName='w-1.5 h-1.5 rounded-[50%] bg-[#E0E0E0]'
        activeDotClassName='w-1.5 h-1.5 rounded-[50%] bg-black'
      />
    </Carousel>
  );
};

const capitalizeFirstLetter = word => {
  if (typeof word !== 'string' || word.length === 0) return '';

  return word.charAt(0).toUpperCase() + word.slice(1);
};

const sortImages = (images = []) => images.filter(({ disabled }) => !disabled).sort((a, b) => a.position - b.position);

export default ProductImageCarousel;
